import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Copy,
  FileText,
  Globe,
  Lock,
  Mail,
  Share2,
  Shield,
  Sparkles,
  User,
} from "lucide-react";
import PolicyDeskRailHead from "../components/policy/PolicyDeskRailHead";
import PolicyDeskSiblingNav from "../components/policy/PolicyDeskSiblingNav";
import { PolicySectionAccordion } from "../components/policy/PolicySectionAccordion";
import {
  PRIVACY_POLICY_META,
  PRIVACY_PROMISES,
  PRIVACY_SECTIONS,
} from "../utils/privacyPolicyContent";
import "../privacy-policy.css";

const sectionIcons = {
  collect: FileText,
  use: Sparkles,
  share: Share2,
  security: Lock,
  choices: User,
  more: Globe,
};

const Privacy = () => {
  const contentRef = useRef(null);

  const [openSections, setOpenSections] = useState(() => new Set(["collect"]));
  const [activeSection, setActiveSection] = useState("collect");
  const [copied, setCopied] = useState(false);

  const toggleSection = (id) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setActiveSection(id);
  };

  const jumpToSection = (id) => {
    setOpenSections((prev) => new Set(prev).add(id));
    setActiveSection(id);
    const node = document.getElementById(`pp-${id}`);
    node?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id?.startsWith("pp-")) {
          setActiveSection(visible.target.id.replace("pp-", ""));
        }
      },
      { root, rootMargin: "-20% 0px -55% 0px", threshold: [0.1, 0.35, 0.6] }
    );

    PRIVACY_SECTIONS.forEach(({ id }) => {
      const node = document.getElementById(`pp-${id}`);
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, []);

  const copyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText("betbondofficial@gmail.com");
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  }, []);

  return (
    <div className="pp-page">
      <div className="pp-desk">
        <div className="pp-workspace">
          <aside className="pp-rail" aria-label="Policy overview">
            <PolicyDeskRailHead meta={PRIVACY_POLICY_META} icon={<Shield size={18} />} />

            <div className="pp-promises">
              {PRIVACY_PROMISES.map((promise) => (
                <div className="pp-promise" key={promise.id}>
                  <strong>{promise.label}</strong>
                  <span>{promise.detail}</span>
                </div>
              ))}
            </div>

            <nav className="pp-index" aria-label="Sections">
              <p className="pp-index-label">On this page</p>
              <ul>
                {PRIVACY_SECTIONS.map((section) => {
                  const Icon = sectionIcons[section.id] || FileText;
                  return (
                    <li key={section.id}>
                      <button
                        type="button"
                        className={`pp-index-btn${activeSection === section.id ? " pp-index-btn-active" : ""}`}
                        onClick={() => jumpToSection(section.id)}
                      >
                        <span className="pp-index-icon" aria-hidden>
                          <Icon size={11} />
                        </span>
                        <span>{section.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <PolicyDeskSiblingNav currentId="privacy" />

            <div className="pp-rail-contact">
              <Mail size={13} aria-hidden />
              <div>
                <strong>Privacy questions?</strong>
                <button type="button" className="pp-copy-email" onClick={copyEmail}>
                  betbondofficial@gmail.com
                  <Copy size={10} aria-hidden />
                </button>
                {copied ? <em>Copied</em> : null}
              </div>
            </div>
          </aside>

          <div className="pp-content" ref={contentRef}>
            <section className="pp-intro-card">
              <div className="pp-intro-top">
                <span className="pp-intro-badge" aria-hidden>
                  <Shield size={12} />
                </span>
                <span>Overview</span>
              </div>
              <p className="pp-intro-text">{PRIVACY_POLICY_META.intro}</p>
              <p className="pp-intro-note">{PRIVACY_POLICY_META.platformNote}</p>
            </section>

            <div className="pp-sections">
              {PRIVACY_SECTIONS.map((section) => (
                <PolicySectionAccordion
                  key={section.id}
                  section={section}
                  open={openSections.has(section.id)}
                  onToggle={() => toggleSection(section.id)}
                />
              ))}
            </div>

            <footer className="pp-contact-strip">
              <div>
                <p className="pp-contact-eyebrow">Need help?</p>
                <h2>Contact our privacy team</h2>
                <p>For access, correction, or deletion requests, email us with your account details.</p>
              </div>
              <a className="pp-contact-btn" href="mailto:betbondofficial@gmail.com">
                <Mail size={14} aria-hidden />
                betbondofficial@gmail.com
              </a>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
